import { Box, TableBody, TableRow, useMediaQuery } from "@mui/material";
import { CardContainer } from "../../../components/base-components/base-componets";
import { AvatarStyled, Label5v5, ModeCircleContainer, TableCellIconStyled, TableContainer, Title } from "./mode-stats-card.styles";
import { ModeStats } from "../../../api/brawler-stats/brawler-stats.types";
import { screenBreackpoints } from "../../../theme";
import { TableSort } from "../../../components/table-sort/table-sort";
import { Column } from "../../../components/table-sort/table-sort.types";
import { RowLink, TableCellStyled } from "../../../components/table-sort/table-sort.styles";
import { formatPercentaje, formatScore, getImageSrc, getModeColor } from "../../../shared/functions";
import { useTranslation } from "react-i18next";
import { ModeStatsTable } from "./mode-stats-card.types";
import { getUrlWithLang } from "../../../routes/routes.functions";
import { APP_ROUTES } from "../../../routes/routes.constants";
import { MODE_PARAM, TEAM_PARAM } from "../../modes/modes.constants";
import { modeToUrlMode } from "../../../api/mode-stats/mode-stats";
import { useLanguage } from "../../../hooks/language/language";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../../configuration/firebase";

interface Props {
  modeStats: ModeStats[];
}

export const ModeStatsCard: React.FC<Props> = ({
  modeStats,
}: Props) => {
  const isMobile = useMediaQuery(`(max-width:${screenBreackpoints.mobile})`);
  const { t } = useTranslation();
  const { getLanguage } = useLanguage();
  const language = getLanguage()

  const columns: Column<ModeStatsTable>[] = [
    { title: "Avatar", key: "totalBattles", empty: true },
    { title: "Wins %", key: "winRate" },
    { title: "Use %", key: "useRate", tooltip: "% of brawler used on this mode respect other brawlers" },
    { title: "Score", key: "effectivityRate", tooltip: "High win rate and low score is a situational brawler" },
    { title: "Play %", key: "playRate", tooltip: "% of mode play of the brawler respect other modes" },
  ]

  const totalBattles = modeStats.reduce((acc, stat) => acc + stat.totalBattles, 0);
  const modeStatsTable: ModeStatsTable[] = modeStats
    .filter(stat => stat.mode !== null)
    .map((stat) => ({
      ...stat,
      playRate: totalBattles === 0 ? 0 : stat.totalBattles / totalBattles
    }));

  return (
    <Box>
      <Title>
        {t("PAGES.BRAWLER.MODES.TITLE")}
      </Title>
      <CardContainer>
        <TableContainer>
          <TableSort
            data={modeStatsTable}
            columns={columns}
            sortDefault={"playRate"}
            visibleRowsOptions={[8]}
            renderBody={(visibleData) => (
              <TableBody>
                {visibleData.map((mode) => (
                  <TableRow key={`${mode.mode}-${mode.teamComposition}`}
                    component={RowLink}
                    to={getUrlWithLang(language, `${APP_ROUTES.MODES}?${MODE_PARAM}=${modeToUrlMode(mode.mode!)}&${TEAM_PARAM}=${modeToUrlMode(mode.teamComposition!)}`)}>
                    <TableCellIconStyled isMobile={isMobile}>
                      <ModeCircleContainer>
                        <AvatarStyled
                          backgroundColor={getModeColor(mode.mode!)}
                          src={getImageSrc(`/images/modes/${mode.mode!.replace("_5V5", "")}.webp`)}
                          alt={`${mode.mode} Logo`}
                          onError={(e) => {
                            logEvent(analytics, 'image_error', {
                              name: mode.mode,
                            });
                          }}
                        >
                          <AvatarStyled
                            backgroundColor={getModeColor(mode.mode!)}
                            src={getImageSrc('/images/modes/UNTRACKED.webp')}
                            alt={`Unknown Logo`}
                          />
                        </AvatarStyled>
                        {mode.teamComposition === "TEAM_2T_5P" && (
                          <Label5v5>
                            5v5
                          </Label5v5>
                        )}
                      </ModeCircleContainer>
                    </TableCellIconStyled>
                    <TableCellStyled isMobile={isMobile}>
                      {formatPercentaje(mode.winRate)}
                    </TableCellStyled>
                    <TableCellStyled isMobile={isMobile}>
                      {formatPercentaje(mode.useRate)}
                    </TableCellStyled>
                    <TableCellStyled isMobile={isMobile}>
                      {formatScore(mode.effectivityRate)}
                    </TableCellStyled>
                    <TableCellStyled isMobile={isMobile}>
                      {formatPercentaje(mode.playRate)}
                    </TableCellStyled>
                  </TableRow>
                ))}
              </TableBody>
            )}
          />
        </TableContainer>
      </CardContainer>
    </Box>
  );
}
